import React, { useMemo, useState } from 'react';
import { HandCoins } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { KpiCard } from '../../components/commerce/KpiCard';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo } from '../../data/mockData';
import { fileUrl } from '../../lib/upload';

export function WalletAdmin() {
  const { topUpRequests, topUpAction, wallets, walletTransactions, adminWalletAdjust, adminMerchants, referralOwners } = useApp();
  const pending = topUpRequests.filter((r) => r.status === 'PENDING');
  const consumerTotal = Object.entries(wallets).filter(([k]) => k.includes('@')).reduce((s, [, v]) => s + v, 0);
  const merchantTotal = Object.entries(wallets).filter(([k]) => !k.includes('@')).reduce((s, [, v]) => s + v, 0);
  const [adj, setAdj] = useState({ ownerType: 'consumer', ownerKey: '', amount: '', note: '' });
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";

  const consumerOptions = useMemo(() => {
    const keys = new Set();
    walletTransactions.filter((t) => t.ownerType === 'consumer').forEach((t) => keys.add(t.ownerKey));
    topUpRequests.filter((r) => r.ownerType === 'consumer').forEach((r) => keys.add(r.ownerKey));
    Object.values(referralOwners).forEach((o) => keys.add(o.email));
    return [...keys];
  }, [walletTransactions, topUpRequests, referralOwners]);

  const nameFor = (type, key) => (type === 'merchant' ? adminMerchants.find((m) => m.id === key)?.name : null) || key;

  const submitAdjust = () => {
    if (!adj.ownerKey) return;
    adminWalletAdjust({ ownerType: adj.ownerType, ownerKey: adj.ownerKey, ownerName: nameFor(adj.ownerType, adj.ownerKey), amount: adj.amount, note: adj.note });
    setAdj({ ...adj, amount: '', note: '' });
  };

  const reqStatus = { PENDING: 'bg-kelate-wood/10 text-kelate-wood', APPROVED: 'bg-kelate-sage/15 text-kelate-sage', REJECTED: 'bg-kelate-red/10 text-kelate-red' };

  return (
    <div data-testid="admin-wallet" className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Pending Top-Ups" value={pending.length} icon={HandCoins} testId="wallet-kpi-pending" />
        <KpiCard label="Member Balances" value={fmtRM2(consumerTotal)} testId="wallet-kpi-consumers" />
        <KpiCard label="Merchant Ads Balances" value={fmtRM2(merchantTotal)} testId="wallet-kpi-merchants" />
      </div>

      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Top-Up Requests</h3>
        {topUpRequests.length === 0 && <EmptyState icon={HandCoins} title="No top-up requests" description="Member and merchant top-up requests appear here for verification." />}
        <div className="space-y-2">
          {topUpRequests.map((r) => (
            <div key={r.id} data-testid={`topup-row-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-3 text-xs">
              <div>
                <span className="text-sm font-semibold text-kelate-ink">{r.ownerName}</span>
                <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-kelate-soft">{r.ownerType}</span>
                <div className="mt-0.5 text-kelate-soft">{fmtRM2(r.amount)} · {r.method === 'TNG' ? 'TNG eWallet' : 'Bank transfer'} · ref <b className="text-kelate-ink">{r.reference}</b> · {timeAgo(r.createdAt)}</div>
                {r.proof && (
                  <a href={fileUrl(r.proof)} target="_blank" rel="noreferrer" data-testid={`topup-proof-${r.id}`} className="mt-1 inline-block">
                    <img src={fileUrl(r.proof)} alt="Payment proof" className="h-16 rounded-lg border border-kelate-line object-cover" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${reqStatus[r.status]}`}>{r.status}</span>
                {r.status === 'PENDING' && (
                  <>
                    <button data-testid={`wallet-approve-${r.id}`} onClick={() => topUpAction(r.id, 'approve')} className="rounded-lg bg-kelate-sage px-3 py-1.5 text-[11px] font-semibold text-white">Approve & Credit</button>
                    <button data-testid={`wallet-reject-${r.id}`} onClick={() => topUpAction(r.id, 'reject')} className="rounded-lg border border-kelate-red/40 px-3 py-1.5 text-[11px] font-medium text-kelate-red">Reject</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]" data-testid="wallet-adjust">
        <h3 className="text-sm font-semibold text-kelate-ink">Adjust Wallet</h3>
        <p className="mt-0.5 text-xs text-kelate-soft">Credit (positive) or deduct (negative) any member or merchant wallet. Every adjustment is audit-logged and the owner is notified.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <select data-testid="adjust-owner-type" value={adj.ownerType} onChange={(e) => setAdj({ ...adj, ownerType: e.target.value, ownerKey: '' })} className={input}>
            <option value="consumer">Member (consumer)</option>
            <option value="merchant">Merchant (ads)</option>
          </select>
          {adj.ownerType === 'merchant' ? (
            <select data-testid="adjust-owner" value={adj.ownerKey} onChange={(e) => setAdj({ ...adj, ownerKey: e.target.value })} className={input}>
              <option value="">Select merchant</option>
              {adminMerchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          ) : (
            <input data-testid="adjust-owner" list="wallet-consumer-options" placeholder="Member email" value={adj.ownerKey} onChange={(e) => setAdj({ ...adj, ownerKey: e.target.value })} className={input} />
          )}
          <datalist id="wallet-consumer-options">{consumerOptions.map((e) => <option key={e} value={e} />)}</datalist>
          <input data-testid="adjust-amount" type="number" step="0.5" placeholder="Amount (+credit / -deduct)" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className={input} />
          <input data-testid="adjust-note" placeholder="Note (e.g. goodwill credit)" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} className={input} />
          <button data-testid="adjust-submit" onClick={submitAdjust} className="rounded-xl bg-kelate-darkwood text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Apply</button>
        </div>
      </section>

      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Recent Wallet Transactions</h3>
        {walletTransactions.length === 0 ? <p className="text-xs text-kelate-soft" data-testid="wallet-txn-empty">No transactions yet.</p> : (
          <div className="space-y-2">
            {walletTransactions.slice(0, 20).map((t) => (
              <div key={t.id} data-testid={`wallet-txn-${t.id}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-2.5 text-xs">
                <div>
                  <div className="font-medium text-kelate-ink">{nameFor(t.ownerType, t.ownerKey)} <span className="font-normal text-kelate-soft">· {t.note}</span></div>
                  <div className="text-[10px] uppercase tracking-wide text-kelate-soft">{t.type.replace(/_/g, ' ')} · {timeAgo(t.at)}</div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${t.amount >= 0 ? 'text-kelate-sage' : 'text-kelate-red'}`}>{t.amount >= 0 ? '+' : ''}{fmtRM2(t.amount)}</div>
                  <div className="text-[10px] text-kelate-soft">Bal {fmtRM2(t.balanceAfter)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
